<template>
  <div>
    <PageHeader
      :title="$texts('chooseEmployer')"
      :lead="
        $texts('chooseEmployerLead')
      "
    />

    <Container>
      <SearchInput id="employer-search" v-model.lazy="searchTerm" />

      <GroupedList
        v-slot="{ item }"
        :items="items"
        group-field="title"
        sort-field="title"
        :hide-letters="!!searchTerm"
        class="mt-28"
      >
        <a
          class="group flex items-center gap-12 border-b border-b-gray-300 py-15 text-xl font-semibold tracking-wide text-blue-500 hover:bg-gray-100"
          :href="item.url"
          @click.prevent="onClick(item.id, item.url)"
        >
          <div
            class="flex size-24 shrink-0 items-center justify-center rounded-full border border-gray-400 group-hover:border-blue-500"
            :class="{
              '!border-blue-500 !bg-blue-500 !text-white': item.selected,
            }"
          >
            <SpriteSymbol
              name="check"
              class="size-20"
              :class="{
                'hidden group-hover:block': !item.selected,
              }"
            />
          </div>
          <span>{{ item.title }}</span>
        </a>
      </GroupedList>
    </Container>
  </div>
</template>

<script setup lang="ts">
import { notNullish } from '@vueuse/core'

// Same path in every language; language comes from the path prefix
// (same pattern as cassia). Do not use languageMapping with identical
// paths — that broke static extraction and left /de/choose-employer 404.
definePageMeta({
  name: 'choose-employer',
  path: '/choose-employer',
})

const { employerId, setEmployerId } = useVisitorState()
const router = useRouter()
const language = useCurrentLanguage()

const searchTerm = useQueryString('text')

const { data: items } = await useAsyncData(
  `choose-employer-list-${language.value}`,
  () =>
    useGraphqlQuery(
      'searchEmployer',
      { text: searchTerm.value },
      {
        graphqlCaching: {
          client: true,
        },
      },
    ).then((v) =>
      (v.data.searchContent?.hits || [])
        .map((v) =>
          v && v.source && v.source.title && v.source.id && v.source.url
            ? {
                id: v.source.id,
                title: v.source.title,
                url: v.source.url,
                selected: v.source.id === employerId.value,
              }
            : null,
        )
        .filter(notNullish),
    ),
  // Same route for all langs (`/:langPrefix/choose-employer`); refetch on language change.
  { default: () => [], watch: [searchTerm, language] },
)

function onClick(id: string, url: string) {
  setEmployerId(id)
  router.push(url)
}

await renderPageDependencies()
</script>
